import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getWiziQuizzes } from '../components/apiClient';

interface WiziQuiz {
  id: number;
  name: string;
  description: string;
  cover_image: string | null;
  total_marks: number;
  passing_score: number;
  time_limit: number;
  level: string;
  status: string;
  quiz_order: number;
  instructions: string;
  is_published: boolean;
}

const MockQuizList: React.FC = () => {
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState<WiziQuiz[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalQuizzes, setTotalQuizzes] = useState(0);
  const pageSize = 10;

  const defaultCoverImage = 'https://via.placeholder.com/400x200?text=Mock+Quiz';

  useEffect(() => {
    const fetchQuizzes = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getWiziQuizzes(currentPage, pageSize, 'active');
        if (response && response.result) {
          setQuizzes(response.result);
          setTotalQuizzes(response.total || 0);
        }
      } catch (err) {
        console.error('Failed to fetch quizzes:', err);
        setError('Failed to load mock quizzes. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchQuizzes();
  }, [currentPage]);

  const handleQuizClick = (quizId: number) => {
    navigate(`/mock-quiz/${quizId}`);
  };

  const totalPages = Math.ceil(totalQuizzes / pageSize);

  const getLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'elementary': return 'success';
      case 'moderate': return 'warning';
      case 'advance': return 'danger';
      default: return 'secondary';
    }
  };

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f8f9ff', paddingTop: '80px' }}>
      <div className="container py-5">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <h2 className="fw-bold mb-2">
                  <i className="fas fa-clipboard-list me-3 text-primary"></i>
                  Mock Tests
                </h2>
                <p className="text-muted mb-0">
                  Practice with full-length mock tests to improve your performance
                </p>
              </div>
              <div className="text-end">
                <span className="badge bg-primary p-3" style={{ fontSize: '1rem', borderRadius: '10px' }}>
                  {totalQuizzes} Tests Available
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted mt-3">Loading mock tests...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="alert alert-danger border-0" style={{ borderRadius: '15px' }}>
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {/* Quiz Cards Grid */}
        {!loading && !error && (
          <>
            <div className="row g-4">
              {quizzes.map((quiz) => (
                <div key={quiz.id} className="col-md-6 col-lg-3">
                  <div 
                    className="card border-0 shadow-sm h-100" 
                    style={{ borderRadius: '15px', cursor: 'pointer', transition: 'transform 0.2s' }}
                    onClick={() => handleQuizClick(quiz.id)}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <div className="position-relative">
                      <img 
                        src={quiz.cover_image || defaultCoverImage} 
                        className="card-img-top" 
                        alt={quiz.name}
                        style={{ 
                          height: '180px', 
                          objectFit: 'contain', 
                          borderTopLeftRadius: '15px', 
                          borderTopRightRadius: '15px' 
                        }}
                      />
                      <span 
                        className={`badge bg-${getLevelColor(quiz.level)} position-absolute top-0 end-0 m-3`}
                        style={{ fontSize: '0.9rem', padding: '8px 15px', borderRadius: '10px' }}
                      >
                        {quiz.level}
                      </span>
                    </div>
                    <div className="card-body d-flex flex-column">
                      <h5 className="card-title fw-bold mb-3">{quiz.name}</h5>
                      <p className="card-text text-muted small mb-3" style={{ 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        display: '-webkit-box', 
                        WebkitLineClamp: 2, 
                        WebkitBoxOrient: 'vertical' 
                      }}>
                        {quiz.description || 'No description available'}
                      </p>
                      <div className="mt-auto">
                        <div className="d-flex justify-content-between text-muted small mb-3">
                          <span>
                            <i className="fas fa-star text-warning me-1"></i>
                            {quiz.total_marks} Marks
                          </span>
                          <span>
                            <i className="fas fa-clock text-info me-1"></i>
                            {quiz.time_limit} min
                          </span>
                        </div>
                        <button 
                          className="btn btn-primary w-100"
                          style={{ borderRadius: '10px' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuizClick(quiz.id);
                          }}
                        >
                          <i className="fas fa-eye me-2"></i>
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Empty State */}
            {quizzes.length === 0 && (
              <div className="text-center py-5">
                <i className="fas fa-inbox text-muted" style={{ fontSize: '4rem', opacity: '0.3' }}></i>
                <h4 className="text-muted mt-4">No Mock Tests Available</h4>
                <p className="text-muted">Please check back later for new tests.</p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="row mt-5">
                <div className="col-12">
                  <nav aria-label="Quiz pagination">
                    <ul className="pagination justify-content-center">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                          style={{ borderRadius: '10px 0 0 10px' }}
                        >
                          <i className="fas fa-chevron-left"></i>
                        </button>
                      </li>
                      {[...Array(totalPages)].map((_, index) => (
                        <li key={index + 1} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => setCurrentPage(index + 1)}
                          >
                            {index + 1}
                          </button>
                        </li>
                      ))}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          style={{ borderRadius: '0 10px 10px 0' }}
                        >
                          <i className="fas fa-chevron-right"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MockQuizList;
